import { useEffect, useRef } from "react";
import { FormikErrors } from "formik";

/**
 * Recursively find the first error key in Formik nested errors.
 */
const getFirstErrorKey = (
  errors: FormikErrors<any>,
  parentKey = "",
): string | null => {
  for (const key in errors) {
    if (Object.prototype.hasOwnProperty.call(errors, key)) {
      const value = errors[key];
      const fullKey = parentKey
        ? Array.isArray(errors)
          ? `${parentKey}[${key}]`
          : `${parentKey}.${key}`
        : key;

      if (typeof value === "string") {
        return fullKey;
      }

      if (typeof value === "object" && value !== null) {
        const nestedKey = getFirstErrorKey(value as FormikErrors<any>, fullKey);
        if (nestedKey) return nestedKey;
      }
    }
  }

  return null;
};

/**
 * A robust hook to automatically scroll into view and focus the first element
 * with a Formik validation error when a submit attempt is made.
 *
 * @param errors - Formik errors object (formik.errors)
 * @param submitCount - Formik submit count (formik.submitCount)
 * @param isSubmitting - Optional: Formik isSubmitting state
 */
export const useScrollToFormikError = (
  errors: FormikErrors<any>,
  submitCount: number,
  isSubmitting?: boolean,
) => {
  const lastFocusedSubmitCount = useRef(0);

  useEffect(() => {
    // Only attempt to scroll/focus if:
    // 1. We have at least one submit attempt
    // 2. We haven't successfully focused for this specific submit attempt yet
    // 3. We have actual errors to focus on
    // 4. If isSubmitting is provided, we wait until validation/submission finished (isSubmitting === false)

    const hasErrors = Object.keys(errors).length > 0;
    const isReady = isSubmitting === undefined ? true : !isSubmitting;
    const isNewSubmit = submitCount > lastFocusedSubmitCount.current;

    if (isNewSubmit && hasErrors && isReady) {
      const firstErrorKey = getFirstErrorKey(errors);

      if (firstErrorKey) {
        const element =
          document.getElementsByName(firstErrorKey)[0] ||
          document.getElementById(firstErrorKey);

        if (element) {
          // Defer so DOM/scroll containers are ready (e.g. modal, tab, or re-render)
          const timeoutId = setTimeout(() => {
            element.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });

            const htmlElement = element as HTMLElement;
            htmlElement.focus?.();

            if (document.activeElement !== htmlElement) {
              const focusableChild = htmlElement.querySelector(
                'input:not([type="hidden"]), select, textarea, [tabindex="0"], button',
              ) as HTMLElement;
              focusableChild?.focus?.();
            }
          }, 100);

          // Mark this submitCount as "handled" so we don't refocus if errors change again
          // (e.g. while typing) unless they submit again.
          lastFocusedSubmitCount.current = submitCount;

          return () => clearTimeout(timeoutId);
        }
      }
    }
  }, [submitCount, errors, isSubmitting]);
};
